import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';

export default function DeliveryEarnings() {
  const nav = useNavigate();
  const [data, setData] = useState({ balance: 0, deliveriesCount: 0, tipsTotal: 0, orders: [], withdrawals: [] });
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const availableToWithdraw = data.balance;
  const totalFromDeliveries = data.orders.reduce((sum, order) => sum + (order.deliveryPrice || 0), 0);
  const totalWithdrawn = (data.withdrawals || [])
    .filter(item => item.status === 'paid' || item.status === 'requested')
    .reduce((sum, item) => sum + (item.amount || 0), 0);

  const loadEarnings = () => {
    setLoading(true);
    api.get('/delivery/earnings')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadEarnings();
  }, []);

  const withdraw = async () => {
    if (data.balance <= 0) { toast.error('Saldo insuficiente'); return; }
    setWithdrawing(true);
    try {
      const response = await api.post('/delivery/withdraw', { amount: data.balance });
      setData(prev => ({
        ...prev,
        balance: response.data.balance,
        withdrawals: response.data.withdrawals,
      }));
      toast.success(response.data.message || `Saque de R$ ${(response.data.withdrawals?.[0]?.amount || 0).toFixed(2)} solicitado!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao solicitar saque');
    } finally {
      setWithdrawing(false);
    }
  };

  const todayEarnings = data.orders
    .filter(o => new Date(o.deliveredAt).toDateString() === new Date().toDateString())
    .reduce((s, o) => s + o.deliveryPrice, 0);

  return (
    <div className="screen bg-gray-900 text-white flex flex-col">
      <div className="px-6 pt-12 pb-6">
        <button onClick={() => nav('/entregador/dashboard')} className="text-2xl mb-4 block">←</button>
        <h1 className="text-2xl font-black">Meus Ganhos</h1>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="text-4xl animate-spin">🔥</div></div>
      ) : (
        <div className="flex-1 px-4 overflow-y-auto pb-6">
          {/* Balance card */}
          <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-3xl p-6 mb-4 text-center">
            <div className="text-green-200 text-sm mb-1">Saldo disponível</div>
            <div className="text-5xl font-black mb-1">R$ {availableToWithdraw.toFixed(2)}</div>
            <div className="text-green-300 text-xs">1 saque por dia • pagamento em até 6h</div>
            <button
              onClick={withdraw}
              disabled={withdrawing || availableToWithdraw <= 0}
              className="mt-4 bg-white text-green-700 font-bold px-6 py-2 rounded-full text-sm hover:bg-green-50 disabled:opacity-50 active:scale-95 transition-all"
            >
              {withdrawing ? 'Processando...' : '💸 Sacar Agora'}
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gray-800 rounded-2xl p-4 text-center">
              <div className="text-gray-400 text-xs mb-1">Hoje</div>
              <div className="text-primary font-black text-xl">R$ {todayEarnings.toFixed(2)}</div>
            </div>
            <div className="bg-gray-800 rounded-2xl p-4 text-center">
              <div className="text-gray-400 text-xs mb-1">Gorjetas</div>
              <div className="text-white font-black text-xl">R$ {data.tipsTotal.toFixed(2)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gray-800 rounded-2xl p-4 text-center">
              <div className="text-gray-400 text-xs mb-1">Total em entregas</div>
              <div className="text-white font-black text-xl">R$ {totalFromDeliveries.toFixed(2)}</div>
            </div>
            <div className="bg-gray-800 rounded-2xl p-4 text-center">
              <div className="text-gray-400 text-xs mb-1">Total em saques</div>
              <div className="text-white font-black text-xl">R$ {totalWithdrawn.toFixed(2)}</div>
            </div>
          </div>

          {/* Transaction history */}
          <h3 className="font-bold text-gray-300 text-sm mb-3">Histórico de entregas</h3>
          {data.orders.length === 0 && (
            <div className="text-center py-8 text-gray-600">
              <p className="text-4xl mb-2">📦</p>
              <p className="text-sm">Nenhuma entrega concluída ainda</p>
            </div>
          )}
          <div className="space-y-2">
            {data.orders.map(o => (
              <div key={o.id} className="bg-gray-800 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{o.brandEmoji}</span>
                  <div>
                    <div className="text-sm font-semibold">{o.brandName} {o.sizeLabel}</div>
                    <div className="text-xs text-gray-400">
                      {o.deliveredAt ? new Date(o.deliveredAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-green-400 font-bold">+R$ {o.deliveryPrice?.toFixed(2)}</div>
                  {(o.tipAmount || 0) > 0 && (
                    <div className="text-[11px] text-emerald-300">gorjeta +R$ {o.tipAmount.toFixed(2)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h3 className="font-bold text-gray-300 text-sm mt-6 mb-3">Solicitações de saque</h3>
          {!(data.withdrawals || []).length && (
            <div className="text-center py-6 text-gray-600">
              <p className="text-sm">Nenhum saque solicitado ainda</p>
            </div>
          )}
          <div className="space-y-2">
            {(data.withdrawals || []).map(item => (
              <div key={item.id} className="bg-gray-800 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Saque solicitado</div>
                  <div className="text-xs text-gray-400">
                    {new Date(item.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {item.estimatedPaymentAt && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      Pagamento até {new Date(item.estimatedPaymentAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-green-400 font-bold">R$ {item.amount.toFixed(2)}</div>
                  <div className="text-[11px] text-yellow-300">pendente</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
